"use client";

import React from "react";
import LatestFinancialGrid from "./LatestFinancialGrid";
import BFLLink from "@repo/shared-ui/BFLLink";
import type { FinancialNewsItem } from "../../interfaces/LatestFinancialResult.interface";
import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/web-store";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";


interface LatestFinancialResultClientProps {
  label: string;
  href: string;
  stocks: FinancialNewsItem[];
  sectionTitle?: string;
  componentPosition?: string;
  openInNewTab?: boolean | string;
}

const LatestFinancialResultClient: React.FC<LatestFinancialResultClientProps> = ({
  stocks,
  label,
  href,
  sectionTitle,
  componentPosition,
  openInNewTab= "false",
}) => {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const resolvedSectionTitle = sectionTitle ?? "Latest financial results";

  const handleViewAllClick = () => {
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        await trackComponent({
          componentName: "ipo_card_sku_descriptive",
          sectionTitle: resolvedSectionTitle,
          eventType: dlEventConstants.CLICK,
          clickType: "ipo_card_sku_descriptive_link_click",
          ctaText: label,
          destinationUrl: href,
          productCategory: "investment",
          cleverTapOverrides: {
            EP_REFERENCE_ID: "latest_financial_results",
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            ...(componentPosition && { EP_SR_NUMBER: componentPosition }),
            EP_ENTRYPOINT: "horizontal/entrypoint",
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track latest financial view all click", error);
      }
    })();
  };

  return (
    <div className="flex flex-col gap-3">
      <LatestFinancialGrid
        stocks={stocks}
        sectionTitle={resolvedSectionTitle}
        componentPosition={componentPosition}
      />
      {isLoggedIn && stocks.length >= 5 && (
        <BFLLink openInNewTab={openInNewTab} href={href}>
        <div className="max-w-fit cursor-pointer border-0 bg-transparent px-0 text-left transition-opacity duration-200 ease-linear">
          <button
            type="button"
            className="text-link-primary h-[18px] cursor-pointer text-[14px] font-[500px]"
            onClick={handleViewAllClick}
          >
            {label}
          </button>
        </div>
      </BFLLink>
      )}
    </div>
  );
};

export default LatestFinancialResultClient;